import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { CONTACT } from "../lib/constants";

const ease = [0.76, 0, 0.24, 1];

const FAQS = [
  {
    q: "When do sessions begin?",
    a: "Weekly sessions start from 8 August 2026 in online mode (evening slot). A free orientation session is held in the 1st week of August — details are emailed after registration.",
  },
  {
    q: "Is this program suitable for working professionals?",
    a: "Yes. Sessions run in the evening and are recorded, so working aspirants can attend live or catch up later. The personalised roadmap is calibrated to your available hours from Day 1.",
  },
  {
    q: "What is the fee and what does it cover?",
    a: "The launch price is ₹5,999 (regular ₹8,999). It covers the entire year of live evening sessions, personalised study roadmap, weekly & monthly target sheets, doubt-clearing clinics, monthly current-affairs sessions, answer-writing evaluation and the session notes library. No hidden add-ons.",
  },
  {
    q: "Do you cover Optional subjects?",
    a: "The core programme focuses on GS, CSAT, Essay, Ethics and Current Affairs. Optional guidance is available on request and depends on the subject — reach out on WhatsApp to discuss your Optional choice.",
  },
  {
    q: "How do I attend the free orientation?",
    a: "Register through the form on this page. Within 24 hours you will receive an email with the orientation link (Google Meet / Zoom) and joining instructions for the 1st week of August.",
  },
  {
    q: "Can I speak to the mentor before joining?",
    a: `Absolutely. WhatsApp Himanshu Sir directly at ${CONTACT.phone} for a short call — questions about your background, target year, or preparation strategy are welcome before you decide.`,
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 lg:py-40 bg-cream-dim border-t border-ink-15"
    >
      <div className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-16">
        {/* Header — centered per reference */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease }}
          className="text-center mb-14 md:mb-20"
        >
          <p className="text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-saffron font-semibold">
            Questions
          </p>
          <h2
            className="mt-4 font-display font-light text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-[-0.03em] text-ink"
            style={{ fontVariationSettings: '"SOFT" 60' }}
          >
            Everything you'd ask <br />
            <span
              className="italic"
              style={{ color: "#B29347", fontVariationSettings: '"SOFT" 100' }}
            >
              before saying yes.
            </span>
          </h2>
        </motion.div>

        {/* Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1, ease }}
        >
          <Accordion
            type="single"
            collapsible
            defaultValue="q-0"
            className="space-y-3 md:space-y-4"
          >
            {FAQS.map((item, idx) => (
              <AccordionItem
                key={idx}
                value={`q-${idx}`}
                data-testid={`faq-item-${idx}`}
                className="group bg-cream-warm border border-ink-15 rounded-2xl overflow-hidden data-[state=open]:border-saffron/70 transition-colors duration-300"
              >
                <AccordionTrigger className="w-full px-6 md:px-8 py-5 md:py-6 text-left hover:no-underline">
                  <span
                    className="font-display text-lg md:text-2xl leading-snug text-ink pr-4"
                    style={{ fontVariationSettings: '"SOFT" 40' }}
                  >
                    {item.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 md:px-8 pb-6 md:pb-7 -mt-1">
                  <p className="text-base md:text-lg text-ink/70 leading-relaxed font-light max-w-3xl">
                    {item.a}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Sub-note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3, ease }}
          className="mt-14 text-center text-sm md:text-base text-ink/55 font-light"
        >
          Still have questions? Reach out on WhatsApp at{" "}
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-whatsapp-link"
            className="text-saffron underline underline-offset-4 hover:text-saffron-deep font-medium"
          >
            +91 {CONTACT.phone}
          </a>{" "}
          — replies are personal.
        </motion.p>
      </div>
    </section>
  );
}
